import {logUtishaDebug, logUtishaError, logUtishaTrace} from 'src/app/util/log';
import {HTMLVideoElementCanvasRenderer} from './HTMLVideoElementCanvasRenderer';
import {ZoomOptions} from '../../../model/ZoomOptions';

/**
 * Renderer for drawing an HTMLVideoElement on a canvas with zoom capabilities
 */
export class ZoomableCanvasRenderer extends HTMLVideoElementCanvasRenderer {
  protected zoomOptions: ZoomOptions = {
    enabled: false,
    x: 0.5,
    y: 0.5,
    factor: 2
  };

  /**
   * Creates a new ZoomableCanvasRenderer with a video element
   * @param videoElement Video element to render
   * @param priority Drawing priority
   * @param initialZoomOptions Initial zoom options (optional)
   */
  static createWithVideoElement(
    videoElement: HTMLVideoElement,
    priority = 10,
    initialZoomOptions?: ZoomOptions
  ): ZoomableCanvasRenderer {
    const renderer = new ZoomableCanvasRenderer(videoElement, priority);

    if (initialZoomOptions) {
      renderer.setZoomOptions(initialZoomOptions);
    }

    return renderer;
  }

  /**
   * Sets zoom options for the renderer
   * @param options ZoomOptions to apply
   */
  setZoomOptions(options: ZoomOptions): void {
    this.zoomOptions = {
      ...this.zoomOptions,
      ...options
    };
    logUtishaDebug(`ZoomableCanvasRenderer[${this.id}]: zoom options updated: ${JSON.stringify(this.zoomOptions)}`);
  }

  /**
   * Renders the video element to the canvas with zoom applied if enabled
   */
  render(): void {
    logUtishaTrace(`ZoomableCanvasRenderer[${this.id}]: rendering`);
    if (!this.canvas || !this.videoElement) return;

    const ctx = this.canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    try {
      // Clear the canvas first
      ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

      if (this.zoomOptions.enabled && this.zoomOptions.factor && this.zoomOptions.factor > 1) {
        // Calculate zoom area
        const factor = this.zoomOptions.factor;
        const x = this.zoomOptions.x || 0.5;
        const y = this.zoomOptions.y || 0.5;

        // Calculate source dimensions (the part of the video we want to show)
        const sourceWidth = this.videoElement.videoWidth / factor;
        const sourceHeight = this.videoElement.videoHeight / factor;

        // Calculate source position (center it around the zoom point)
        const sourceX = (x * this.videoElement.videoWidth) - (sourceWidth / 2);
        const sourceY = (y * this.videoElement.videoHeight) - (sourceHeight / 2);

        // Ensure source coordinates stay within bounds of the video
        const clampedSourceX = Math.max(0, Math.min(this.videoElement.videoWidth - sourceWidth, sourceX));
        const clampedSourceY = Math.max(0, Math.min(this.videoElement.videoHeight - sourceHeight, sourceY));

        // Draw the zoomed portion
        ctx.drawImage(
          this.videoElement,
          clampedSourceX, clampedSourceY, sourceWidth, sourceHeight,
          0, 0, this.canvas.width, this.canvas.height
        );

        logUtishaTrace(`ZoomableCanvasRenderer[${this.id}]: rendered with zoom factor ${factor} at ${x},${y}`);
      } else {
        // When zoom is not enabled, use the parent class render method
        super.render();
      }
    } catch (error) {
      logUtishaDebug(`ZoomableCanvasRenderer[${this.id}]: error rendering - ${error}`);
    }
  }

  /**
   * Gets the name of this renderer
   */
  get name(): string {
    return ZoomableCanvasRenderer.NAME;
  }

  static get NAME() {
    return 'ZoomableCanvasRenderer';
  }
}
